// Comandos do agregado Usuário — as ações que NÃO tocam na senha (editar
// nome/login/perfil, inativar/reativar, excluir). Criar e redefinir senha
// passam por rotas dedicadas no servidor, porque o hash bcrypt só existe lá.
//
// O executor recebe `ctx.usuarioAtual` (id do usuário logado, só no servidor)
// e impede que alguém tranque a si mesmo pra fora — inclusive trocando o
// PRÓPRIO perfilId pra um Perfil sem gravação em Configuração (issue #71):
// sem isso, `editar` era o único jeito de se auto-trancar que nenhuma guarda
// cobria (definirAtivo/excluir já se protegiam).

import type { Snapshot } from "../domain/snapshot.ts";
import {
  definirAtivoUsuario,
  editarUsuario,
  excluirUsuario,
} from "../domain/usuarios.ts";
import { temGravacaoEmConfiguracao } from "../domain/perfis.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import { comoObjeto, exigirBooleano, exigirTexto, texto } from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

/** O Perfil (se existir e estiver ativo) tem gravação em Configuração? */
function perfilAdministraConfiguracao(snapshot: Snapshot, perfilId: string | null): boolean {
  if (perfilId == null) return false;
  const perfil = snapshot.perfis.find((p) => p.id === perfilId);
  return perfil?.ativo === true && temGravacaoEmConfiguracao(snapshot.permissoesModulo, perfilId);
}

export type ComandoUsuario =
  | { tipo: "editar"; id: string; nome: string; login: string; perfilId: string | null }
  | { tipo: "definirAtivo"; id: string; ativo: boolean }
  | { tipo: "excluir"; id: string };

export function executarComandoUsuario(
  snapshot: Snapshot,
  cmd: ComandoUsuario,
  ctx: ContextoComando,
): Snapshot {
  const ehVoceMesmo = ctx.usuarioAtual != null && ctx.usuarioAtual === cmd.id;
  switch (cmd.tipo) {
    case "editar":
      if (ehVoceMesmo && !perfilAdministraConfiguracao(snapshot, cmd.perfilId)) {
        throw new Error(
          "Você não pode trocar o seu próprio perfil para um sem gravação em Configuração.",
        );
      }
      return editarUsuario(snapshot, cmd.id, {
        nome: cmd.nome,
        login: cmd.login,
        perfilId: cmd.perfilId,
      });
    case "definirAtivo":
      if (ehVoceMesmo && !cmd.ativo) {
        throw new Error("Você não pode inativar o seu próprio usuário.");
      }
      return definirAtivoUsuario(snapshot, cmd.id, cmd.ativo);
    case "excluir":
      if (ehVoceMesmo) {
        throw new Error("Você não pode excluir o seu próprio usuário.");
      }
      return excluirUsuario(snapshot, cmd.id);
    default:
      return assertNunca(cmd);
  }
}

export function validarComandoUsuario(valor: unknown): ComandoUsuario {
  const c = comoObjeto(valor, "Comando de usuário");
  switch (c.tipo) {
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        nome: texto(c.nome),
        login: texto(c.login),
        perfilId: typeof c.perfilId === "string" && c.perfilId !== "" ? c.perfilId : null,
      };
    case "definirAtivo":
      return {
        tipo: "definirAtivo",
        id: exigirTexto(c.id, "id"),
        ativo: exigirBooleano(c.ativo, "ativo"),
      };
    case "excluir":
      return { tipo: "excluir", id: exigirTexto(c.id, "id") };
    default:
      throw new Error(`Tipo de comando de usuário desconhecido: ${String(c.tipo)}.`);
  }
}
